// src/components/RuleEditor.tsx
import React from 'react';
import { Condition, FormDefinition, CheckDefinition, ValidationRule, getAllQuestionIds } from '../../../../interfaces/dynamicForms/FormBuilder';
import FormInput from './FormInput';
import ConditionEditor from './ConditionEditor';
import CheckEditor from './CheckEditor';
import { Button } from 'react-bootstrap';
import "./FormBuilder.css";

interface RuleEditorProps {
    rule: ValidationRule;
    formDefinition: FormDefinition;
    updateRule: (ruleId: string, updatedRule: Partial<ValidationRule>) => void;
    deleteRule: (ruleId: string) => void;
}

const RuleEditor: React.FC<RuleEditorProps> = ({
    rule,
    formDefinition,
    updateRule,
    deleteRule,
}) => {
    const allQuestionIds = getAllQuestionIds(formDefinition);

    const handleChange = (field: keyof ValidationRule, value: any) => {
        updateRule(rule.id, { ...rule, [field]: value });
    };

    const handleWhenUpdate = (newConditions: Condition[]) => {
        handleChange('when', newConditions);
    };

    const updateCheckAtIndex = (index: number, updatedCheck: CheckDefinition) => {
        const newChecks = [...(rule.checks || [])];
        newChecks[index] = updatedCheck;
        handleChange('checks', newChecks);
    };

    const addCheck = () => {
        const newCheck: CheckDefinition = {
             type: 'FIELD_CHECK',
             field: '',
             operator: 'EQUALS',
             value: ''
        };
        handleChange('checks', [...(rule.checks || []), newCheck]);
    };

    const removeCheck = (index: number) => {
        handleChange('checks', rule.checks.filter((_, i) => i !== index));
    };

    return (
        <>
            <div className="form_cerate_step">
                <div className="top top_bar_flx border_tittle mby_16">
                    <div className="top_bar_tittle sm_tittle">
                        <h2>Edit Rule: {rule.id}</h2>
                    </div>
                    <div className="top_right_flx">
                        <Button variant='danger' className='btn_sm' onClick={() => deleteRule(rule.id)}>Delete Rule</Button>
                    </div>
                </div>

                <div className="row">
                    {/* Rule ID */}
                    <div className="col-md-6 labelBlock">
                        <div className='custom_input_flled'>
                            <FormInput
                                label="Rule ID"
                                id={`rule-${rule.id}-id`}
                                value={rule.id}
                                onChange={(e) => handleChange('id', e.target.value)}
                                required
                            />
                            <div className="stats_filled mt-1">
                                <p>A unique internal identifier for this rule. It's best not to change this.</p>
                            </div>
                        </div>
                    </div>
                    {/* Error Field */}
                    <div className='col-md-6 labelBlock'>
                        <div className='custom_input_flled'>
                            <label htmlFor={`rule-${rule.id}-errorField`}>Error Field</label>
                            <select
                                style={{maxWidth: 'unset'}}
                                id={`rule-${rule.id}-errorField`}
                                value={rule.errorField}
                                onChange={(e) => handleChange('errorField', e.target.value)}
                                className="sm_input filled_white"
                                required
                            >
                                <option value="">Select Field...</option>
                                {allQuestionIds.map(id => (
                                    <option key={id} value={id}>{id}</option>
                                ))}
                            </select>
                            <div className="stats_filled mt-1">
                                <p>Choose which question will display the error message when this rule fails.</p>
                            </div>
                        </div>
                    </div>
                    {/* Description */}
                    <div className="col-md-6 labelBlock">
                        <div className="custom_input_flled">
                            <label htmlFor={`rule-${rule.id}-desc`}>Description</label>
                            <textarea
                                id={`rule-${rule.id}-desc`}
                                value={rule.description}
                                onChange={(e) => handleChange('description', e.target.value)}
                                rows={2}
                                className="filled_white"
                            />
                            <div className="stats_filled mt-1">
                                <p>An internal note explaining what this rule does. This will not be shown to form users.</p>
                            </div>
                        </div>
                    </div>
                    {/* Error Message */}
                    <div className="col-md-6 labelBlock">
                        <div className="custom_input_flled">
                            <label htmlFor={`rule-${rule.id}-errMsg`}>Error Message</label>
                            <textarea
                                id={`rule-${rule.id}-errMsg`}
                                value={rule.errorMessage}
                                onChange={(e) => handleChange('errorMessage', e.target.value)}
                                rows={2}
                                required
                                className='filled_white'
                            />
                            <div className="stats_filled mt-1">
                                <p>The exact message shown to a user if their answer is invalid according to this rule.</p>
                            </div>
                        </div>
                    </div>
                </div>

                <ConditionEditor
                    conditions={rule.when || []}
                    formDefinition={formDefinition}
                    updateConditions={handleWhenUpdate}
                    title="Apply This Rule When..."
                />
                <div className="stats_filled mt-2">
                    <p>This rule will only run if <strong>all</strong> conditions above are met. If no conditions are set, the rule always applies.</p>
                </div>

                <div className="common_card">
                    <div className="top top_bar_flx border_tittle mby_16">
                        <div className="top_bar_tittle sm_tittle flex-column align-items-start">
                            <h2>Checks ({rule.checks?.length || 0})</h2>
                            <p>Define the validation tests here. If the conditions above are met, these checks are performed. If <strong>any</strong> check fails, the error message is shown.</p>
                        </div>
                        <div className="top_right_flx">
                            <Button variant='primary' className='btn_sm' onClick={addCheck}>Add Check</Button>
                        </div>
                    </div>
                    <div>
                        {rule.checks?.map((check, index) => (
                            <CheckEditor
                                key={index}
                                check={check}
                                allQuestionIds={allQuestionIds}
                                updateCheck={(updatedCheck) => updateCheckAtIndex(index, updatedCheck)}
                                removeCheck={() => removeCheck(index)}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

export default RuleEditor;